import type { App } from 'vue'
import { defineNuxtPlugin, onNuxtReady, useClock } from '#imports'

const INTERVAL_MS = 30_000
const MAX_BACKOFF_MS = 300_000
const MAX_RTT_MS = 2000
const REQUEST_TIMEOUT_MS = 10_000

export function startClockSync(app: App) {
    const { setServerTime } = useClock()
    let timer: ReturnType<typeof setTimeout> | undefined
    let deadline: ReturnType<typeof setTimeout> | undefined
    let request: AbortController | undefined
    let inFlight = false
    let failures = 0
    let stopped = false

    const canPoll = () => document.visibilityState === 'visible' && navigator.onLine

    function schedule() {
        clearTimeout(timer)
        timer = undefined
        if (stopped || !canPoll()) return

        timer = setTimeout(sync, failures ? Math.min(INTERVAL_MS * 2 ** failures, MAX_BACKOFF_MS) : INTERVAL_MS)
    }

    async function sync() {
        if (stopped || inFlight || !canPoll()) return
        inFlight = true
        // ofetch's own timeout stops at the headers; this also bounds the body.
        request = new AbortController()
        deadline = setTimeout(() => request?.abort(), REQUEST_TIMEOUT_MS)

        try {
            const t0 = performance.now()
            const { now } = await $fetch<{ now: unknown }>('/api/public/time', { retry: 0, signal: request.signal })
            const t1 = performance.now()
            if (stopped) return
            if (typeof now !== 'number') throw new TypeError('Malformed server time')

            if (t1 - t0 <= MAX_RTT_MS) setServerTime(now, (t0 + t1) / 2)
            failures = 0
        }
        catch {
            failures++
        }
        finally {
            clearTimeout(deadline)
            inFlight = false
            schedule()
        }
    }

    const onVisibilityChange = () => document.visibilityState === 'visible' ? sync() : schedule()
    const onPageShow = (event: PageTransitionEvent) => event.persisted && sync()

    onNuxtReady(() => {
        if (stopped) return
        document.addEventListener('visibilitychange', onVisibilityChange)
        window.addEventListener('online', sync)
        window.addEventListener('offline', schedule)
        window.addEventListener('pageshow', onPageShow)
        sync()
    })

    app.onUnmount(() => {
        stopped = true
        clearTimeout(timer)
        clearTimeout(deadline)
        request?.abort()
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('online', sync)
        window.removeEventListener('offline', schedule)
        window.removeEventListener('pageshow', onPageShow)
    })
}

export default defineNuxtPlugin({
    name: 'clock-sync',
    setup(nuxtApp) {
        // Skip under Vitest so the test app doesn't poll; tests call startClockSync.
        if (import.meta.env.VITEST) return

        startClockSync(nuxtApp.vueApp)
    },
})
